import { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import {
  Users,
  ChevronLeft,
  Search,
  Plus,
  Download,
  Upload,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Clock,
  Calendar,
  Mail,
  Phone,
  MapPin,
  Edit,
  Trash2,
  MoreVertical,
  Star,
  Shield,
  FileText,
  X
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Badge } from '../../components/ui/Badge';
import { Input, Select } from '../../components/ui/Input';
import { useSeries } from '../../App';

// Mock membership data
const mockMemberships = [
  {
    id: 1,
    member_type: 'driver',
    name: 'Brad Sweet',
    car_number: '49',
    team: 'Kasey Kahne Racing',
    membership_type: 'Full-Time',
    status: 'active',
    expires: '2026-12-31',
    email: 'brad@kkr.com',
    phone: '(555) 123-4567',
    location: 'Grass Valley, CA',
    joined: '2018-01-01',
    points_eligible: true,
    emergency_contact: 'John Sweet',
    emergency_phone: '(555) 987-6543'
  },
  {
    id: 2,
    member_type: 'driver',
    name: 'David Gravel',
    car_number: '2',
    team: 'Big Game Motorsports',
    membership_type: 'Full-Time',
    status: 'active',
    expires: '2026-12-31',
    email: 'david@bgm.com',
    phone: '(555) 234-5678',
    location: 'Watertown, CT',
    joined: '2019-01-01',
    points_eligible: true,
    emergency_contact: 'Jane Gravel',
    emergency_phone: '(555) 876-5432'
  },
  {
    id: 3,
    member_type: 'driver',
    name: 'Carson Macedo',
    car_number: '41',
    team: 'Jason Johnson Racing',
    membership_type: 'Full-Time',
    status: 'active',
    expires: '2026-12-31',
    email: 'carson@jjr.com',
    phone: '(555) 345-6789',
    location: 'Lemoore, CA',
    joined: '2020-01-01',
    points_eligible: true,
    emergency_contact: 'Mike Macedo',
    emergency_phone: '(555) 765-4321'
  },
  {
    id: 4,
    member_type: 'driver',
    name: 'Donny Schatz',
    car_number: '15',
    team: 'Tony Stewart Racing',
    membership_type: 'Full-Time',
    status: 'active',
    expires: '2026-12-31',
    email: 'donny@tsr.com',
    phone: '(555) 456-7890',
    location: 'Fargo, ND',
    joined: '2006-01-01',
    points_eligible: true,
    emergency_contact: 'Erica Schatz',
    emergency_phone: '(555) 654-3210'
  },
  {
    id: 5,
    member_type: 'driver',
    name: 'Logan Schuchart',
    car_number: '1S',
    team: 'Shark Racing',
    membership_type: 'Full-Time',
    status: 'pending',
    expires: '2026-12-31',
    email: 'logan@sharkracing.com',
    phone: '(555) 567-8901',
    location: 'Hanover, PA',
    joined: '2016-01-01',
    points_eligible: false,
    emergency_contact: 'Bobby Allen',
    emergency_phone: '(555) 543-2109'
  },
  {
    id: 6,
    member_type: 'driver',
    name: 'James McFadden',
    car_number: '9',
    team: 'Kasey Kahne Racing',
    membership_type: 'Full-Time',
    status: 'active',
    expires: '2026-12-31',
    email: 'james@kkr.com',
    phone: '(555) 678-9012',
    location: 'Warrnambool, AUS',
    joined: '2022-01-01',
    points_eligible: true,
    emergency_contact: 'Sarah McFadden',
    emergency_phone: '(555) 432-1098'
  },
  {
    id: 7,
    member_type: 'driver',
    name: 'Kyle Larson',
    car_number: '57',
    team: 'Silva Motorsports',
    membership_type: 'Part-Time',
    status: 'active',
    expires: '2026-12-31',
    email: 'kyle@larsonracing.com',
    phone: '(555) 789-0123',
    location: 'Elk Grove, CA',
    joined: '2021-01-01',
    points_eligible: false,
    emergency_contact: 'Katelyn Larson',
    emergency_phone: '(555) 321-0987'
  },
  {
    id: 8,
    member_type: 'driver',
    name: 'Rico Abreu',
    car_number: '24',
    team: 'Rico Abreu Racing',
    membership_type: 'Part-Time',
    status: 'expired',
    expires: '2025-12-31',
    email: 'rico@rabracing.com',
    phone: '(555) 890-1234',
    location: 'St. Helena, CA',
    joined: '2015-01-01',
    points_eligible: false,
    emergency_contact: 'Alfonso Abreu',
    emergency_phone: '(555) 210-9876'
  },
];

// Mock team memberships
const mockTeams = [
  { id: 1, name: 'Kasey Kahne Racing', owner: 'Kasey Kahne', status: 'active', drivers: 2, expires: '2026-12-31' },
  { id: 2, name: 'Big Game Motorsports', owner: 'Tod Quiring', status: 'active', drivers: 1, expires: '2026-12-31' },
  { id: 3, name: 'Jason Johnson Racing', owner: 'Bobbi Johnson', status: 'active', drivers: 1, expires: '2026-12-31' },
  { id: 4, name: 'Tony Stewart Racing', owner: 'Tony Stewart', status: 'active', drivers: 1, expires: '2026-12-31' },
  { id: 5, name: 'Shark Racing', owner: 'Bobby Allen', status: 'pending', drivers: 2, expires: '2026-12-31' },
];

// Status badge component
function StatusBadge({ status }) {
  const config = {
    active: { variant: 'success', icon: CheckCircle2, label: 'Active' },
    pending: { variant: 'warning', icon: Clock, label: 'Pending' },
    expired: { variant: 'error', icon: XCircle, label: 'Expired' },
    suspended: { variant: 'error', icon: XCircle, label: 'Suspended' }
  };

  const { variant, icon: Icon, label } = config[status] || config.pending;

  return (
    <Badge variant={variant} className="flex items-center gap-1">
      <Icon className="w-3 h-3" />
      {label}
    </Badge>
  );
}

// Membership type badge
function MembershipTypeBadge({ type }) {
  return (
    <Badge
      variant={type === 'Full-Time' ? 'primary' : 'neutral'}
      size="sm"
    >
      {type}
    </Badge>
  );
}

// Member card component
function MemberCard({ member, onEdit, onView }) {
  const [showMenu, setShowMenu] = useState(false);

  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 hover:shadow-sm transition-all">
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center border border-slate-200">
            <span className="font-bold text-slate-600">#{member.car_number}</span>
          </div>
          <div>
            <h3 className="font-semibold text-slate-800">{member.name}</h3>
            <p className="text-sm text-slate-500">{member.team}</p>
          </div>
        </div>
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 rounded hover:bg-slate-100"
          >
            <MoreVertical className="w-4 h-4 text-slate-400" />
          </button>
          {showMenu && (
            <div className="absolute right-0 top-8 bg-white border border-slate-200 rounded-lg shadow-lg py-1 z-10 min-w-[120px]">
              <button
                onClick={() => { onView(member); setShowMenu(false); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
              >
                <FileText className="w-4 h-4" />
                View Details
              </button>
              <button
                onClick={() => { onEdit(member); setShowMenu(false); }}
                className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 flex items-center gap-2"
              >
                <Edit className="w-4 h-4" />
                Edit
              </button>
              <button className="w-full px-3 py-2 text-left text-sm hover:bg-slate-50 text-red-600 flex items-center gap-2">
                <Trash2 className="w-4 h-4" />
                Remove
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="flex items-center gap-2 mb-3">
        <StatusBadge status={member.status} />
        <MembershipTypeBadge type={member.membership_type} />
        {member.points_eligible && (
          <Badge variant="success" size="sm" className="flex items-center gap-1">
            <Star className="w-3 h-3" />
            Points
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-2 gap-2 text-sm">
        <div className="flex items-center gap-2 text-slate-500">
          <Calendar className="w-3.5 h-3.5" />
          <span>Exp: {new Date(member.expires).toLocaleDateString()}</span>
        </div>
        <div className="flex items-center gap-2 text-slate-500">
          <MapPin className="w-3.5 h-3.5" />
          <span className="truncate">{member.location}</span>
        </div>
      </div>
    </div>
  );
}

// Team card component
function TeamCard({ team }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-4 hover:border-slate-300 transition-colors">
      <div className="flex items-center justify-between mb-2">
        <h3 className="font-semibold text-slate-800">{team.name}</h3>
        <StatusBadge status={team.status} />
      </div>
      <p className="text-sm text-slate-500 mb-3">Owner: {team.owner}</p>
      <div className="flex items-center justify-between text-sm">
        <span className="text-slate-600">{team.drivers} driver(s)</span>
        <span className="text-slate-400">Exp: {new Date(team.expires).toLocaleDateString()}</span>
      </div>
    </div>
  );
}

// Member detail modal
function MemberDetailModal({ member, isOpen, onClose }) {
  if (!isOpen || !member) return null;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-lg">
        <div className="flex items-center justify-between p-4 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-800">Member Details</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>

        <div className="p-4 space-y-4">
          {/* Header */}
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-slate-100 to-slate-200 flex items-center justify-center border-2 border-slate-200">
              <span className="font-bold text-xl text-slate-600">#{member.car_number}</span>
            </div>
            <div>
              <h2 className="text-xl font-bold text-slate-800">{member.name}</h2>
              <p className="text-slate-500">{member.team}</p>
              <div className="flex items-center gap-2 mt-1">
                <StatusBadge status={member.status} />
                <MembershipTypeBadge type={member.membership_type} />
              </div>
            </div>
          </div>

          {/* Contact Info */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-slate-700 text-sm">Contact Information</h4>
            <div className="grid grid-cols-1 gap-2 text-sm">
              <div className="flex items-center gap-2">
                <Mail className="w-4 h-4 text-slate-400" />
                <a href={`mailto:${member.email}`} className="text-rms-accent hover:underline">{member.email}</a>
              </div>
              <div className="flex items-center gap-2">
                <Phone className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">{member.phone}</span>
              </div>
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-slate-400" />
                <span className="text-slate-600">{member.location}</span>
              </div>
            </div>
          </div>

          {/* Membership Info */}
          <div className="bg-slate-50 rounded-lg p-4 space-y-3">
            <h4 className="font-medium text-slate-700 text-sm">Membership Details</h4>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <span className="text-slate-500">Member Since</span>
                <p className="font-medium text-slate-700">{new Date(member.joined).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="text-slate-500">Expires</span>
                <p className="font-medium text-slate-700">{new Date(member.expires).toLocaleDateString()}</p>
              </div>
              <div>
                <span className="text-slate-500">Points Eligible</span>
                <p className="font-medium text-slate-700">{member.points_eligible ? 'Yes' : 'No'}</p>
              </div>
              <div>
                <span className="text-slate-500">Type</span>
                <p className="font-medium text-slate-700">{member.membership_type}</p>
              </div>
            </div>
          </div>

          {/* Emergency Contact */}
          <div className="bg-red-50 rounded-lg p-4 space-y-2">
            <h4 className="font-medium text-red-700 text-sm flex items-center gap-2">
              <Shield className="w-4 h-4" />
              Emergency Contact
            </h4>
            <p className="text-sm text-slate-700">{member.emergency_contact}</p>
            <p className="text-sm text-slate-600">{member.emergency_phone}</p>
          </div>
        </div>

        <div className="flex justify-end gap-2 p-4 border-t border-slate-200">
          <Button variant="ghost" onClick={onClose}>Close</Button>
          <Button>Edit Member</Button>
        </div>
      </div>
    </div>
  );
}

export default function Memberships() {
  const { seriesSlug } = useParams();
  const currentSeries = useSeries();

  const [activeTab, setActiveTab] = useState('drivers');
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [typeFilter, setTypeFilter] = useState('all');
  const [selectedMember, setSelectedMember] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);

  const series = currentSeries || { name: 'Unknown Series', color: '#3b5998' };

  // Filter members
  const filteredMembers = mockMemberships.filter(m => {
    const matchesSearch = m.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.car_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      m.team.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || m.status === statusFilter;
    const matchesType = typeFilter === 'all' || m.membership_type === typeFilter;
    return matchesSearch && matchesStatus && matchesType;
  });

  // Stats
  const activeMembers = mockMemberships.filter(m => m.status === 'active').length;
  const pendingMembers = mockMemberships.filter(m => m.status === 'pending').length;
  const fullTimeMembers = mockMemberships.filter(m => m.membership_type === 'Full-Time').length;
  const expiringThisMonth = mockMemberships.filter(m => {
    const expDate = new Date(m.expires);
    const now = new Date();
    return expDate.getMonth() === now.getMonth() && expDate.getFullYear() === now.getFullYear();
  }).length;

  const handleViewMember = (member) => {
    setSelectedMember(member);
    setShowDetailModal(true);
  };

  const handleEditMember = (member) => {
    // Navigate to edit or open edit modal
    console.log('Edit member:', member);
  };

  return (
    <div className="animate-fade-in">
      {/* Header */}
      <div className="mb-6">
        <Link
          to={`/${seriesSlug}/season`}
          className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-700 mb-4"
        >
          <ChevronLeft className="w-4 h-4" />
          Back to Dashboard
        </Link>

        <div className="flex items-start justify-between">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <Users className="w-6 h-6 text-emerald-500" />
              <h1 className="text-2xl font-bold text-slate-800">Memberships</h1>
            </div>
            <p className="text-slate-500">Manage driver and team memberships for {series.name}</p>
          </div>

          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm">
              <Upload className="w-4 h-4 mr-1" />
              Import
            </Button>
            <Button variant="ghost" size="sm">
              <Download className="w-4 h-4 mr-1" />
              Export
            </Button>
            <Button>
              <Plus className="w-4 h-4 mr-1" />
              Add Member
            </Button>
          </div>
        </div>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{activeMembers}</p>
                <p className="text-xs text-slate-500">Active Members</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
                <Clock className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{pendingMembers}</p>
                <p className="text-xs text-slate-500">Pending Approval</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
                <Star className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{fullTimeMembers}</p>
                <p className="text-xs text-slate-500">Full-Time Drivers</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-red-100 flex items-center justify-center">
                <AlertCircle className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-800">{expiringThisMonth}</p>
                <p className="text-xs text-slate-500">Expiring Soon</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-4">
        <button
          onClick={() => setActiveTab('drivers')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            activeTab === 'drivers'
              ? 'bg-rms-accent text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Drivers ({mockMemberships.length})
        </button>
        <button
          onClick={() => setActiveTab('teams')}
          className={`px-4 py-2 rounded-lg font-medium text-sm transition-colors ${
            activeTab === 'teams'
              ? 'bg-rms-accent text-white'
              : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
          }`}
        >
          Teams ({mockTeams.length})
        </button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex-1 min-w-[200px] relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Search by name, car number, or team..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10"
              />
            </div>
            <Select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="w-40"
            >
              <option value="all">All Statuses</option>
              <option value="active">Active</option>
              <option value="pending">Pending</option>
              <option value="expired">Expired</option>
            </Select>
            <Select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              className="w-40"
            >
              <option value="all">All Types</option>
              <option value="Full-Time">Full-Time</option>
              <option value="Part-Time">Part-Time</option>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Content */}
      {activeTab === 'drivers' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filteredMembers.length > 0 ? (
            filteredMembers.map((member) => (
              <MemberCard
                key={member.id}
                member={member}
                onView={handleViewMember}
                onEdit={handleEditMember}
              />
            ))
          ) : (
            <div className="col-span-full">
              <Card>
                <CardContent className="py-12 text-center">
                  <Users className="w-12 h-12 text-slate-300 mx-auto mb-4" />
                  <h3 className="text-lg font-medium text-slate-600 mb-2">No Members Found</h3>
                  <p className="text-slate-400">Try adjusting your search or filters</p>
                </CardContent>
              </Card>
            </div>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {mockTeams.map((team) => (
            <TeamCard key={team.id} team={team} />
          ))}
        </div>
      )}

      {/* Member Detail Modal */}
      <MemberDetailModal
        member={selectedMember}
        isOpen={showDetailModal}
        onClose={() => setShowDetailModal(false)}
      />
    </div>
  );
}
